import React, { useMemo, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { get, groupBy, isArray, isEmpty, sortBy } from "lodash";
import VinWithType from "../../../../../components/Common/Display/VinWithType";
import Location from "../../../../../components/Common/Display/Location";

type TProps = {
    loads: any[];
    t: any;
    mode?: 'group' | 'load' | 'inbound';
};

type TOrderRow = {
    key: string;
    from_location: any;
    to_location: any;
    dealer_name: string;
    vin_number: string;
    brand: string;
    model: string;
};

const getLocationLabel = (location: any): string => {
    const city = get(location, "city");
    const code = get(location, "location_code");
    const landmark = get(location, "landmark");
    const country = get(location, "country_sort") || get(location, "country");

    if ( !isEmpty(city) && !isEmpty(country) ) {
        return `${ city } (${ country })`;
    }
    if ( !isEmpty(city) ) {
        return String(city);
    }
    if ( !isEmpty(code) ) {
        return String(code);
    }
    if ( !isEmpty(landmark) ) {
        return String(landmark);
    }
    return "-";
};

export default function GroupedOrdersByRoute(props: TProps) {
    const { loads, t, mode } = props;
    const safeLoads = isArray(loads) ? loads : [];
    const isInboundMode = mode === 'inbound' || safeLoads.some((load: any) => isArray(get(load, 'alp_inbound_load_orders')) && get(load, 'alp_inbound_load_orders').length > 0);

    const grouped = useMemo(() => {
        const rows: TOrderRow[] = [];

        safeLoads.forEach((load: any, loadIdx: number) => {
            const from_location = get(load, "from_location");
            const to_location = get(load, "to_location");
            const orderRows = isArray(get(load, "alp_outbound_load_orders"))
                ? get(load, "alp_outbound_load_orders")
                : (isArray(get(load, "alp_inbound_load_orders")) ? get(load, "alp_inbound_load_orders") : []);

            orderRows.forEach((orderRow: any, orderIdx: number) => {
                const dealer_name = isInboundMode
                    ? String(get(orderRow, "from_supplier_compound") || get(load, "supplier_compound") || "-")
                    : String(get(load, "dealer_name") || get(load, "supplier_compound") || "-");
                const order = get(orderRow, "order");
                const vin_number = String(get(order, "vin_number") || "-");
                const brand = String(get(order, "brand.title") || "-");
                const modelTitle = get(order, "brand_model.title");
                const modelVersion = get(order, "brand_model.version_code");
                const model = String(
                    !isEmpty(modelVersion) ? `${ modelTitle ?? "-" } - ${ modelVersion }` : ( modelTitle ?? "-" )
                );

                rows.push({
                    key: `${ load?.id ?? load?.guid ?? loadIdx }___${ get(order, "id") ?? vin_number }___${ orderIdx }`,
                    from_location,
                    to_location,
                    dealer_name,
                    vin_number,
                    brand,
                    model,
                });
            });
        });

        const groups = groupBy(rows, (row: TOrderRow) => {
            const fromId = String(get(row, "from_location.id") || "");
            const toId = String(get(row, "to_location.id") || "");
            return `${ fromId }___${ toId }`;
        });

        const keys = Object.keys(groups || {});
        const items = keys.map((key: string) => {
            const groupRows = groups[key] || [];
            const from = get(groupRows, "0.from_location");
            const to = get(groupRows, "0.to_location");
            const fromLabel = getLocationLabel(from);
            const toLabel = getLocationLabel(to);

            return {
                key,
                from,
                to,
                fromLabel,
                toLabel,
                rows: groupRows,
                count: groupRows.length,
            };
        });

        return sortBy(items, [(i: any) => i.fromLabel, (i: any) => i.toLabel]);
    }, [safeLoads]);

    const [activeIndex, setActiveIndex] = useState<any>(() => {
        return grouped.length > 0 ? [0] : [];
    });

    if ( safeLoads.length <= 0 ) {
        return <div className="text-muted small">{ t("no records found") }</div>;
    }

    if ( grouped.length <= 0 ) {
        return <div className="text-muted small">{ t("no records found") }</div>;
    }

    return (
        <Accordion
            multiple
            activeIndex={ activeIndex }
            onTabChange={ (e: any) => setActiveIndex(e.index) }
        >
            { grouped.map((g: any, gIdx: number) => {
                const header = (
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <strong className="text-uppercase">
                                { g.fromLabel } → { g.toLabel }
                            </strong>
                            <span className="text-muted small">
                                ({ g.count } { t("vehicles") })
                            </span>
                        </div>
                        <div className="text-muted small d-none d-md-block">
                            <span className="me-1"><Location item={ g.from } titled={ 1 } /></span>
                            <span><Location item={ g.to } titled={ 1 } /></span>
                        </div>
                    </div>
                );

                return (
                    <AccordionTab key={ g.key || gIdx } header={ header }>
                        <div className="table-responsive">
                            <table className={ "fl-table w-full" }>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{ isInboundMode ? t("supplier compound") : t("dealer") }</th>
                                        <th>{ t("vin number") }</th>
                                        <th>{ t("brand") }</th>
                                        <th>{ t("model") }</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    { ( g.rows || [] ).map((row: TOrderRow, idx: number) => (
                                        <tr key={ row.key ?? idx }>
                                            <td>{ idx + 1 }</td>
                                            <td className="text-uppercase">{ row.dealer_name || "-" }</td>
                                            <td className="text-uppercase">
                                                <VinWithType vinNumber={ row.vin_number } />
                                            </td>
                                            <td className="text-uppercase">{ row.brand || "-" }</td>
                                            <td className="text-uppercase">{ row.model || "-" }</td>
                                        </tr>
                                    )) }
                                </tbody>
                            </table>
                        </div>
                    </AccordionTab>
                );
            }) }
        </Accordion>
    );
}

